import React, { useState } from "react";
import { Box, Typography, Button, Link, TextField, Alert } from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

const DL = {
  bgDark: "#0f1419",
  bgMedium: "#1a1f26",
  bgLight: "#242930",
  green: "#62f481",
  greenEmerald: "#44aa5a",
  textPrimary: "#f9f9cf",
  textSecondary: "#99a1af",
  textMuted: "#6a7282",
  border: "rgba(255,255,255,0.08)",
  borderSubtle: "rgba(255,255,255,0.15)",
  borderGreen: "rgba(98,244,129,0.3)",
  divider: "rgba(98,244,129,0.2)",
  red: "#ff6467",
};

function CheckEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [identifier, setIdentifier] = useState(searchParams.get("email") || "");
  const [isResending, setIsResending] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleResend = async () => {
    if (!identifier.trim()) {
      setErrorMessage("Enter the email address you used to sign up so we can resend the verification email.");
      setSuccessMessage("");
      return;
    }

    setIsResending(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SC_API_BASE_URL}/api/auth/resend-verification`,
        { identifier: identifier.trim() }
      );
      setSuccessMessage(
        response.data?.message || "If an unverified account matches that email, we've sent a new verification email."
      );
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message || "We couldn't resend the verification email right now. Please try again."
      );
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: DL.bgDark,
        background: `radial-gradient(ellipse at 50% 0%, rgba(98,244,129,0.06) 0%, ${DL.bgDark} 60%)`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      {/* Back link */}
      <Box sx={{ width: "100%", maxWidth: 480, mb: 3 }}>
        <Link
          component="button"
          onClick={() => navigate("/signin")}
          underline="none"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            color: DL.textSecondary,
            fontSize: "0.875rem",
            cursor: "pointer",
            "&:hover": { color: DL.textPrimary },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 16 }} />
          Back to Sign In
        </Link>
      </Box>

      {/* Card */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 480,
          backgroundColor: DL.bgMedium,
          border: `1px solid ${DL.border}`,
          borderRadius: "12px",
          p: { xs: 3, sm: 4 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            backgroundColor: "rgba(98,244,129,0.08)",
            border: `1px solid ${DL.borderGreen}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
          }}
        >
          <MailOutlineIcon sx={{ fontSize: 36, color: DL.green }} />
        </Box>

        <Typography
          component="h1"
          sx={{
            fontSize: "1.5rem",
            fontWeight: 700,
            color: DL.textPrimary,
            mb: 1.5,
          }}
        >
          Check Your Email
        </Typography>

        <Typography
          sx={{
            fontSize: "0.9375rem",
            color: DL.textSecondary,
            lineHeight: 1.6,
            mb: 2,
          }}
        >
          We've sent a verification link to your email address. Please click the
          link to verify your account before signing in.
        </Typography>

        <Typography
          sx={{
            fontSize: "0.875rem",
            color: DL.textMuted,
            lineHeight: 1.6,
            mb: 3,
          }}
        >
          If you don't see it, check your spam, junk, or promotions folder.
          Verification links expire after 24 hours, and resending will send you
          a fresh link.
        </Typography>

        <TextField
          fullWidth
          label="Email"
          value={identifier}
          onChange={(event) => setIdentifier(event.target.value)}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              backgroundColor: DL.bgDark,
              borderRadius: "8px",
              "& fieldset": { borderColor: DL.borderSubtle },
              "&:hover fieldset": { borderColor: DL.borderGreen },
              "&.Mui-focused fieldset": { borderColor: DL.green, borderWidth: "1.5px" },
            },
            "& .MuiInputBase-input": {
              color: DL.textPrimary,
              fontSize: "0.9rem",
            },
            "& .MuiInputLabel-root": { color: DL.textSecondary, fontSize: "0.9rem" },
            "& .MuiInputLabel-root.Mui-focused": { color: DL.green },
          }}
        />

        {successMessage && (
          <Alert
            severity="success"
            sx={{
              width: "100%",
              mb: 2,
              borderRadius: "8px",
              backgroundColor: "rgba(98,244,129,0.1)",
              color: DL.green,
              "& .MuiAlert-icon": { color: DL.green },
            }}
          >
            {successMessage}
          </Alert>
        )}

        {errorMessage && (
          <Alert
            severity="error"
            sx={{
              width: "100%",
              mb: 2,
              borderRadius: "8px",
              backgroundColor: "rgba(255,100,103,0.1)",
              color: DL.red,
              border: "1px solid rgba(255,100,103,0.25)",
              "& .MuiAlert-icon": { color: DL.red },
            }}
          >
            {errorMessage}
          </Alert>
        )}

        <Button
          fullWidth
          variant="outlined"
          onClick={handleResend}
          disabled={isResending}
          sx={{
            borderColor: DL.borderGreen,
            color: DL.green,
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 700,
            py: 1.25,
            fontSize: "0.9375rem",
            mb: 2,
            "&:hover": {
              borderColor: DL.green,
              backgroundColor: "rgba(98,244,129,0.08)",
            },
            "&.Mui-disabled": {
              borderColor: DL.borderSubtle,
              color: DL.textMuted,
            },
          }}
        >
          {isResending ? "Sending New Link..." : "Resend Verification Email"}
        </Button>

        <Button
          fullWidth
          variant="contained"
          onClick={() => navigate("/signin")}
          sx={{
            background: "linear-gradient(to right, #10b981, #059669)",
            color: "#fff",
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 700,
            py: 1.25,
            fontSize: "0.9375rem",
            boxShadow: "none",
            "&:hover": {
              background: "linear-gradient(to right, #059669, #047857)",
              boxShadow: "none",
            },
          }}
        >
          Go to Sign In
        </Button>
      </Box>
    </Box>
  );
}

export default CheckEmailPage;
